import PropTypes from 'prop-types';
import React, { useLayoutEffect, useRef, useState } from 'react';
import { defineMessages } from 'react-intl';

import Message from 'components/Message';
import { bem } from 'helpers/class-name';
import './SmartSpotFillTooltip.scss';
import { SMARTSPOT_CATEGORY_TYPE } from './SmartTools.helpers';

const { block, element } = bem('smartspot-fill-tooltip');

const messages = defineMessages({
  directFill: {
    id: 'smart-tools.smart-spot-fill-tooltip.direct-fill',
    defaultMessage: 'Direct fill: {directFill}',
  },
  smartSpotFill: {
    id: 'smart-tools.smart-spot-fill-tooltip.smartspot-fill',
    defaultMessage: 'SmartSpot fill: {smartspotFill}',
  },
});

const DEFAULT_TOOLTIP_WIDTH = 180;

const SmartSpotFillTooltip = ({ datum, x = 0 }) => {
  const { directFill, smartspotFill, noData, category } = datum;
  const tooltipRef = useRef(null);
  const [tooltipWidth, setTooltipWidth] = useState(DEFAULT_TOOLTIP_WIDTH);

  useLayoutEffect(() => {
    if (tooltipRef.current) {
      setTooltipWidth(tooltipRef.current.offsetWidth);
    }
  }, [directFill, smartspotFill]);

  if (noData) {
    return null;
  }

  const hasSmartspotFill = smartspotFill != null && smartspotFill !== 0;
  const hasDirectFill = directFill != null && directFill !== 0;

  if (!hasSmartspotFill && !hasDirectFill) {
    return null;
  }

  const maxTooltipWidth = 250; // Buffer for any language
  const tooltipHeight = 70;
  const isRightSide =
    category === SMARTSPOT_CATEGORY_TYPE.NINETY_TO_ONE_HUNDRED || category === SMARTSPOT_CATEGORY_TYPE.EIGHTY_TO_NINETY;
  // Use offsetWidth to determine postion (to prevent overflow)
  const tooltipX = isRightSide
    ? x - tooltipWidth * 0.75 // Move left for right-side bars so that it's not overhanging
    : x - tooltipWidth / 4; // Centered & slightly to the right
  const tooltipY = -50; // Fixed position above all bars (negative y places it above the chart area)

  const foreignObjectProps = {
    x: tooltipX,
    y: tooltipY,
    width: maxTooltipWidth,
    height: tooltipHeight,
  };

  return (
    <foreignObject {...foreignObjectProps}>
      <div ref={tooltipRef} className={block()}>
        {hasSmartspotFill && (
          <div className={element('row', { smartspot: true })}>
            <Message
              className={element('legend-icon', { smartspot: true })}
              message={messages.smartSpotFill}
              values={{ smartspotFill }}
            />
          </div>
        )}
        {hasDirectFill && (
          <div className={element('row', { direct: true })}>
            <Message
              className={element('legend-icon', { direct: true })}
              message={messages.directFill}
              values={{ directFill }}
            />
          </div>
        )}
      </div>
    </foreignObject>
  );
};

SmartSpotFillTooltip.propTypes = {
  datum: PropTypes.shape({
    category: PropTypes.string,
    directFill: PropTypes.number,
    smartspotFill: PropTypes.number,
    noData: PropTypes.number,
  }),
  x: PropTypes.number,
};

export default SmartSpotFillTooltip;
